import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Clock, MapPin, Phone } from "lucide-react";
import { SiteLayout } from "./layout";
import { HeroImage } from "./hero-image";
import { Reveal } from "./reveal";
import { SERVICE_PAGES, SITE, servicePagePath } from "@/lib/site";
import { SPORSMAL_SIDER, sporsmalPath, type SporsmalSide } from "@/lib/sporsmal";

/**
 * Side som svarer på ett spørsmål folk stiller AI-assistenter om bilpleie i
 * Drammen. Overskriften er spørsmålet, første avsnitt er svaret, og alt står
 * i klartekst i HTML-en. Ingen trekkspill, ingen tekst som først vises etter
 * et klikk: en assistent som må lete nedover siden, siterer en annen side.
 */
export function QuestionPage({ side }: { side: SporsmalSide }) {
  const andre = SPORSMAL_SIDER.filter((s) => s.slug !== side.slug);
  const tjenester = side.tjenester
    .map((slug) => SERVICE_PAGES.find((p) => p.slug === slug))
    .filter((p): p is (typeof SERVICE_PAGES)[number] => Boolean(p));

  return (
    <SiteLayout>
      <section className="relative min-h-[70svh] flex items-end overflow-hidden">
        <HeroImage />
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-10 pb-16 sm:pb-24 pt-32">
          <Reveal>
            <div className="eyebrow text-primary/90">{SITE.name} · Spørsmål og svar · Drammen</div>
          </Reveal>
          <Reveal delay={140}>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-white mt-6 text-balance max-w-3xl">
              {side.sporsmal}
            </h1>
          </Reveal>
          <Reveal delay={280}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/90">{side.svar}</p>
          </Reveal>
          <Reveal delay={420}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                search={{ service: side.preselect } as never}
                className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
              >
                {side.bookTekst} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 text-sm text-white/85 hover:text-primary">
                <Phone className="h-4 w-4 text-primary" /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {side.seksjoner.map((seksjon, i) => (
        <section key={seksjon.tittel} className={i % 2 === 1 ? "py-16 sm:py-24 border-y border-border bg-secondary/30" : "py-16 sm:py-24"}>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-balance">{seksjon.tittel}</h2>
            </Reveal>
            {seksjon.avsnitt.map((a) => (
              <Reveal key={a.slice(0, 40)} delay={80}>
                <p className="mt-5 text-muted-foreground leading-relaxed max-w-3xl">{a}</p>
              </Reveal>
            ))}
            {seksjon.punkter && (
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {seksjon.punkter.map((p, j) => (
                  <Reveal key={p} delay={j * 60}>
                    <li className="flex gap-3 rounded-xl border border-border bg-card p-4 text-sm text-foreground/90">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {p}
                    </li>
                  </Reveal>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-balance">Flere spørsmål om {side.sporsmal.toLowerCase().includes("dekk") ? "dekkskift" : side.sporsmal.toLowerCase().includes("polering") ? "polering" : "bilvask"} i Drammen</h2>
          </Reveal>
          <dl className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card px-6">
            {side.oppfolging.map((item) => (
              <div key={item.q} className="py-5">
                <dt className="font-serif text-lg">{item.q}</dt>
                <dd className="mt-2 text-muted-foreground leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Navn, adresse og telefon står likt her, i bunnteksten og i Google-profilen. */}
      <section className="py-16 border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow">Her finner du oss i Drammen</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4">{SITE.name}</h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-3 text-sm">
            <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-5">
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>{SITE.address}</span>
            </div>
            <a href={SITE.phoneHref} className="flex items-start gap-3 rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors">
              <Phone className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>{SITE.phone}</span>
            </a>
            <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-5">
              <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>
                Man–fre 09:00–20:00
                <br />
                Lør 09:00–19:00, søn stengt
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="eyebrow">Les mer om tjenestene</div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tjenester.map((t) => (
              <Link
                key={t.slug}
                to={servicePagePath(t) as any}
                className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors flex items-center justify-between"
              >
                <span className="font-serif text-lg">{t.h1}</span>
                <ArrowRight className="h-4 w-4 text-primary" />
              </Link>
            ))}
          </div>
          <div className="eyebrow mt-12">Andre spørsmål vi får om bilpleie i Drammen</div>
          <div className="mt-6 flex flex-col gap-2">
            {andre.map((s) => (
              <Link
                key={s.slug}
                to={sporsmalPath(s) as any}
                className="text-sm text-foreground/85 hover:text-primary"
              >
                {s.sporsmal} →
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary">← Alle spørsmål og svar</Link>
          </div>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 gradient-radial" />
        <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl sm:text-5xl text-balance">Book time i Drammen i dag</h2>
          <Link
            to="/book"
            search={{ service: side.preselect } as never}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
          >
            {side.bookTekst} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
